import { useEffect, useReducer } from "react";
import { initialState, reducer } from "./state";
import { useToast } from "@chakra-ui/react";
import axios from "axios";
import { Project } from "models/project_type";
import { TaskCard } from "models/tasks_type";
import { UserModelGet } from "models/user_model";
import { validatorEngine } from "helpers/functions";
import { ActionTypeReducer } from "helpers/types";
import { TimerGetType, TimerType } from "models/timer_type";

type changeInputType = ActionTypeReducer<typeof initialState.timer>
type changeSelectsType = ActionTypeReducer<Pick<typeof initialState, 'projectSelected' | 'taskSelected'>>

export const useTimerTracker = () => {
    const toast = useToast({
        position: "top-right",
        duration: 3000,
    })

    const [state, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        (async () => {
            await loadData();
            await loadTimerExec();
        })()
    }, []);

    useEffect(() => {
        loadProjectsByStorage();
    }, [state.projects]);

    useEffect(() => {
        loadTasksByStorage();
    }, [state.tasksList]);

    const getLoggedUser = () => {
        const userData = JSON.parse(localStorage.getItem("user"));
        dispatch({ type: "setLoggedUser", payload: userData });

        return userData as UserModelGet;
    }

    const loadProjects = async () => {
        const user = state.loggedUser ?? getLoggedUser();

        const url = process.env.REACT_APP_API_URL + `/projeto/list/${user?.idEmpresa}`;
        return axios.get<Project[]>(url);
    };

    const loadTimers = async () => {
        const user = state.loggedUser ?? getLoggedUser();

        const url = process.env.REACT_APP_API_URL + `/projeto/timer/${user?.id}`;
        return axios.get<TimerGetType[]>(url);
    };

    const loadTasks = async (projectId: number) => {
        dispatch({ type: "setLoading", payload: true });

        try {
            const url = process.env.REACT_APP_API_URL + `/projeto/task/${projectId}`;
            const tasks = await axios.get<TaskCard[]>(url);

            dispatch({ type: "setTasksList", payload: tasks.data });
        } catch (e) {
            console.error("loadTasks ", e);
            showError({ description: "Erro ao carregar Tasks" });
        } finally {
            dispatch({ type: "setLoading", payload: false });
        }
    };

    const loadTimerExec = async () => {
        dispatch({ type: "setLoading", payload: true });

        try {
            const timers = await loadTimers();

            dispatch({ type: "setTimerList", payload: timers.data });
        } catch (e) {
            console.error("loadTimerExec ", e);
            showError({ description: "Erro ao carregar lista de Timers" });
        } finally {
            dispatch({ type: "setLoading", payload: false });
        }
    };

    const validateCompanyFields = () => {
        const { timer } = state;
        const validations: validatorEngine.type<typeof initialState.timerFormFieldsError> = {
            'initialMoment': {
                test: () => !!timer?.initialMoment,
                errorMessage: "Campo obrigatório"
            },
            'finalMoment': {
                test: () => !!timer?.finalMoment,
                errorMessage: "Campo obrigatório"
            }
        };

        const { isValid, errors } = validatorEngine.build(validations);

        dispatch({
            type: "setTimerFormFieldsError",
            payload: errors,
        });

        return isValid;
    };

    const displayInputError = (type: keyof typeof initialState.timerFormFieldsError) => {
        const error = state.timerFormFieldsError[type];

        return ({
            error: !!error,
            errorMessage: error
        })
    }

    const changeInput = ({ type, payload }: changeInputType) => {
        const time = { ...state.timer, [type]: payload };
        dispatch({ type: "setTimer", payload: time });
    }

    const changeSelects = ({ type, payload }: changeSelectsType) => {
        switch (type) {
            case "projectSelected":
                dispatch({ type: "setProjectSelected", payload });
                break;
            case "taskSelected":
                dispatch({ type: "setTaskSelected", payload });
                break;
        }
    };

    const createTimerExec = async (payload: TimerType) => {
        const timer = state.timer;
        dispatch({ type: "setTimer", payload: { ...timer, initialMoment: null, finalMoment: null } });
        dispatch({ type: "setLoading", payload: true });
        toast.promise(createTimerRequest(payload), {
            success: {
                title: 'Sucesso',
                description: 'Timer registrado com sucesso!',
                onCloseComplete: () => {
                    localStorage.removeItem("timer");
                    dispatch({ type: "setTimer", payload: null });
                    dispatch({ type: "setProjectSelected", payload: null });
                    dispatch({ type: "setTaskSelected", payload: null });
                    dispatch({ type: "setLoading", payload: false });
                    loadTimerExec();
                },
            },
            error: {
                title: 'Erro',
                description: 'Houve um problema, tente mais tarde.',
                onCloseComplete: () => {
                    dispatch({ type: "setTimer", payload: timer });
                    dispatch({ type: "setLoading", payload: false });
                }
            },
            loading: {
                title: 'Carregando',
                description: 'Por favor aguarde...',
            },
        });
    }

    const confirmTimer = async () => {

        if (state.inputType === 'manual' && !validateCompanyFields()) return;

        dispatch({ type: "setLoading", payload: true });

        try {

            if (state.inputType === 'auto') {

                if (!state.timer?.initialMoment) {
                    const payload: TimerType = {
                        ...state.timer,
                        userId: state.loggedUser.id,
                        projectId: state.projectSelected?.id,
                        taskId: state.taskSelected?.id,
                        initialMoment: new Date(),
                    }

                    localStorage.setItem("timer", JSON.stringify(payload));
                    dispatch({ type: "setTimer", payload });

                    return;
                }

                const payload: TimerType = {
                    ...state.timer,
                    userId: state.loggedUser.id,
                    projectId: state.projectSelected?.id,
                    taskId: state.taskSelected?.id,
                    initialMoment: new Date(state.timer.initialMoment),
                    finalMoment: new Date(),
                }

                createTimerExec(payload);
            } else {
                const payload: TimerType = {
                    ...state.timer,
                    userId: state.loggedUser.id,
                    projectId: state.projectSelected?.id,
                    taskId: state.taskSelected?.id,
                }

                createTimerExec(payload);
            }
        } catch (e) {
            console.error("confirmTimer ", e);
            showError({ description: "Erro ao registrar timer" });
        } finally {
            dispatch({ type: "setLoading", payload: false });
        }
    };

    const deleteTimer = async (timer: TimerGetType) => {
        const url = process.env.REACT_APP_API_URL + `/projeto/timer/${timer.id}`;
        return axios.delete(url);
    };

    const deleteTimerExec = async (timer: TimerGetType) => {
        dispatch({ type: "setLoading", payload: true });
        toast.promise(deleteTimer(timer), {
            success: {
                title: 'Sucesso',
                description: 'Timer deletado com sucesso!',
                onCloseComplete: () => {
                    loadTimerExec();
                },
            },
            error: {
                title: 'Erro',
                description: 'Houve um problema, tente mais tarde.',
                onCloseComplete: () => {
                    dispatch({ type: "setLoading", payload: false });
                }
            },
            loading: {
                title: 'Carregando',
                description: 'Por favor aguarde...',
            },
        });
    };

    const createTimerRequest = async (payload: TimerType) => {
        const url = process.env.REACT_APP_API_URL + "/projeto/timer";
        return axios.post(url, payload);
    };

    const switchType = (type: typeof state['inputType']) => {
        dispatch({ type: "setInputType", payload: type });
    };

    const loadTimerByStorage = () => {
        const timer = JSON.parse(localStorage.getItem("timer")) || null;
        dispatch({ type: "setTimer", payload: timer });
    };

    const loadProjectsByStorage = () => {
        if (state.timer?.projectId && state.projectSelected === null && state.projects.length > 0) {

            const project = state.projects.find(p => p.id === state.timer.projectId);
            dispatch({ type: "setProjectSelected", payload: project });
            loadTasks(project.id);
        }
    };

    const loadTasksByStorage = () => {
        if (state.timer?.taskId && state.taskSelected === null && state.tasksList.length > 0) {

            const task = state.tasksList.find(t => t.id === state.timer.taskId);
            dispatch({ type: "setTaskSelected", payload: task });
        }
    };

    const loadData = async () => {
        loadTimerByStorage();

        dispatch({ type: "setLoading", payload: true });
        try {
            const projects = await loadProjects();

            dispatch({ type: "setProjects", payload: projects.data });
        } catch (e) {
            console.error("useEffect.loadData ", e);
            showError({ description: "Erro ao carregar Projetos" });
        } finally {
            dispatch({ type: "setLoading", payload: false });
        }
    };

    const showError = ({ title = "Erro!", description = "Houve um problema, tente mais tarde!" }) => {
        toast({
            title,
            description,
            status: "error",
            isClosable: true,
        });
    };


    return {
        state,
        loadTasks,
        switchType,
        confirmTimer,
        changeInput,
        changeSelects,
        deleteTimerExec,
        displayInputError,
    };
};

export namespace useTimerTracker {
    export type type = ReturnType<typeof useTimerTracker>;
}
